import { Controller } from "@hotwired/stimulus"

// Drives the reveal on the express "You've already started" auto-fill interstitial when the LLM
// auto-fill finishes. Listens for the window-level autofill:complete event (fired by autofill-signal
// once the fill is done, whether via poll or broadcast): rolls the entry-count + minutes-saved numbers
// up (jackpot-number), swaps the read-only cards for the fully-filled set while animating the
// newly-added rows, enables the Continue button, and flips the fill-status pill to a green "done" state
// before fading + collapsing it away. Idempotent — reveals only once — and stops the status poll
// afterwards. Also handles the footer Back / Continue clicks.
export default class extends Controller {
  static targets = [
    "count", "minutes", "countWord", "minuteWord", "cards", "continue",
    "fillStatus", "fillStatusSpinner", "fillStatusIcon", "fillStatusLabel"
  ]
  static values = { frameId: String }

  reveal(event) {
    if (this.revealed) return
    this.revealed = true

    this.stopPolling()

    const detail = event.detail || {}
    this.rollNumber(this.hasCountTarget ? this.countTarget : null, detail.count)
    this.rollNumber(this.hasMinutesTarget ? this.minutesTarget : null, detail.minutes)
    this.setWord(this.hasCountWordTarget ? this.countWordTarget : null, detail.countWord)
    this.setWord(this.hasMinuteWordTarget ? this.minuteWordTarget : null, detail.minuteWord)
    this.swapCards(detail.cardsHtml)
    this.enableContinue()
    this.finishFillStatus()
  }

  continue() {
    const url = this.hasContinueTarget && this.continueTarget.dataset.continueUrl
    if (url) Turbo.visit(url)
  }

  back() {
    history.back()
  }

  // Remove the polled frame's src + poll controller so it stops re-fetching once we've revealed.
  stopPolling() {
    const frame = this.frameIdValue && document.getElementById(this.frameIdValue)
    if (!frame) return
    frame.removeAttribute("src")
    frame.removeAttribute("data-controller")
  }

  rollNumber(element, value) {
    if (!element || value === undefined || value === null) return
    const jackpot = this.application.getControllerForElementAndIdentifier(element, "jackpot-number")
    if (jackpot) jackpot.setValue(String(value))
    else element.textContent = String(value)
  }

  setWord(element, word) {
    if (element && word) element.textContent = word
  }

  swapCards(html) {
    if (!this.hasCardsTarget || !html) return
    const seen = new Set(
      Array.from(this.cardsTarget.querySelectorAll("[data-entry-key]")).map((el) => el.dataset.entryKey)
    )
    this.cardsTarget.innerHTML = html
    this.cardsTarget.querySelectorAll("[data-entry-key]").forEach((el) => {
      if (!seen.has(el.dataset.entryKey)) el.classList.add("cc-entry-enter")
    })
  }

  enableContinue() {
    if (!this.hasContinueTarget) return
    this.continueTarget.disabled = false
    this.continueTarget.classList.remove("opacity-50", "cursor-not-allowed")
  }

  // Flip the "still filling in" pill to a green "done" state, then (after a beat so it registers) fade
  // it out and vertically collapse it to nothing. No-op if the pill isn't on the page (it's only
  // rendered when the fill was still running on load).
  finishFillStatus() {
    if (!this.hasFillStatusTarget) return
    const el = this.fillStatusTarget

    el.classList.remove("border-support-light-300", "bg-white")
    el.classList.add("border-complementary-green-300", "bg-complementary-green-200")
    if (this.hasFillStatusSpinnerTarget) this.fillStatusSpinnerTarget.classList.add("hidden")
    if (this.hasFillStatusIconTarget) this.fillStatusIconTarget.classList.remove("hidden")
    if (this.hasFillStatusLabelTarget) {
      this.fillStatusLabelTarget.textContent = "We're done filling in the info we had"
      this.fillStatusLabelTarget.classList.remove("text-support-dark-700")
      this.fillStatusLabelTarget.classList.add("text-complementary-green-700")
    }

    setTimeout(() => this.collapseFillStatus(el), 1400)
  }

  collapseFillStatus(el) {
    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduceMotion) {
      el.remove()
      return
    }

    // Pin the current height + top margin so we can transition them to zero (fade + shrink away).
    el.style.overflow = "hidden"
    el.style.height = `${el.getBoundingClientRect().height}px`
    el.style.marginTop = window.getComputedStyle(el).marginTop
    void el.offsetHeight // force a reflow so the browser has a start value before we change it
    el.style.transition = "height 450ms ease, opacity 450ms ease, margin-top 450ms ease, " +
      "padding-top 450ms ease, padding-bottom 450ms ease"
    el.style.height = "0px"
    el.style.opacity = "0"
    el.style.marginTop = "0px"
    el.style.paddingTop = "0px"
    el.style.paddingBottom = "0px"
    el.addEventListener("transitionend", () => el.remove(), { once: true })
  }
}
